/**
 * Hook for managing Kanban board mutation operations
 * @remarks
 * Extracted from useKanbanBoard to reduce cognitive complexity.
 * Provides configured mutation functions for boards, phases, and cards.
 * The return object is memoized to maintain referential stability across renders,
 * preventing unnecessary effect triggers when used as a dependency.
 */
import { useMemo } from "react";

import { MESSAGES } from "../constants";

import type { KanbanBoard, KanbanPhase } from "@/generated/graphql";
import {
  useAddPlayerToKanbanMutation,
  useCloseKanbanCardToTerminalStageMutation,
  useCreateKanbanBoardMutation,
  useCreateKanbanPhaseMutation,
  useDeleteKanbanPhaseMutation,
  useMoveKanbanCardMutation,
  useRemovePlayerFromKanbanMutation,
  useReopenKanbanCardMutation,
  useUpdateKanbanBoardMutation,
  useUpdateKanbanCardPositionsMutation,
  useUpdateKanbanPhaseMutation,
  useUpdateKanbanPhasePositionsMutation,
} from "@/generated/graphql";

/**
 * Return type for the useKanbanBoardMutations hook
 */
interface KanbanBoardMutationsResult {
  /** Create a new kanban board */
  createBoardMutation: ReturnType<typeof useCreateKanbanBoardMutation>[0];
  /** Whether board creation is in progress */
  isCreatingBoard: boolean;
  /** Update kanban board details */
  updateBoardMutation: ReturnType<typeof useUpdateKanbanBoardMutation>[0];
  /** Create a new phase/column */
  createPhaseMutation: ReturnType<typeof useCreateKanbanPhaseMutation>[0];
  /** Whether phase creation is in progress */
  isCreatingPhase: boolean;
  /** Update phase details */
  updatePhaseMutation: ReturnType<typeof useUpdateKanbanPhaseMutation>[0];
  /** Delete a phase */
  deletePhaseMutation: ReturnType<typeof useDeleteKanbanPhaseMutation>[0];
  /** Update phase positions for reordering */
  updatePhasePositionsMutation: ReturnType<
    typeof useUpdateKanbanPhasePositionsMutation
  >[0];
  /** Add a player to the kanban board */
  addPlayerMutation: ReturnType<typeof useAddPlayerToKanbanMutation>[0];
  /** Move a card between phases */
  moveCardMutation: ReturnType<typeof useMoveKanbanCardMutation>[0];
  /** SE-5496: close a card into a terminal stage with an outcome + reason */
  closeCardMutation: ReturnType<
    typeof useCloseKanbanCardToTerminalStageMutation
  >[0];
  /** SE-5496: reopen a closed card into a working stage */
  reopenCardMutation: ReturnType<typeof useReopenKanbanCardMutation>[0];
  /** Reorder cards within a phase (SE-4685 rank drag-reorder) */
  updateCardPositionsMutation: ReturnType<
    typeof useUpdateKanbanCardPositionsMutation
  >[0];
  /** Remove a player from the board */
  removePlayerMutation: ReturnType<typeof useRemovePlayerFromKanbanMutation>[0];
}

/**
 * Hook for kanban board mutations with optimistic responses
 * @param board - Current board for optimistic responses
 * @param phases - Current phases for optimistic responses
 * @param onError - Critical error handler (triggers full-screen error for board creation failures)
 * @param onOperationError - Non-critical error handler (triggers toast for mutation failures)
 * @returns Memoized object with configured mutation functions for boards, phases, and cards
 */
const useKanbanBoardMutations = (
  board: KanbanBoard | null,
  phases: KanbanPhase[],
  onError: (message: string) => void,
  onOperationError: (message: string) => void
): KanbanBoardMutationsResult => {
  // Board mutations
  const [createBoardMutation, { loading: isCreatingBoard }] =
    useCreateKanbanBoardMutation({
      refetchQueries: ["ListMyKanbanBoards"],
      awaitRefetchQueries: true,
      onError: () => {
        onError("Failed to create board. Please try again.");
      },
    });

  const [updateBoardMutation] = useUpdateKanbanBoardMutation({
    optimisticResponse: variables => ({
      __typename: "Mutation",
      updateKanbanBoard: {
        __typename: "KanbanBoard",
        ...board!,
        name: variables.input.name!,
        updatedAt: new Date().toISOString(),
      },
    }),
    onError: () => {
      onOperationError("Failed to update board name. Please try again.");
    },
  });

  // Phase mutations
  const [createPhaseMutation, { loading: isCreatingPhase }] =
    useCreateKanbanPhaseMutation({
      refetchQueries: ["ListKanbanPhases"],
      awaitRefetchQueries: false,
      onError: () => {
        onOperationError("Failed to add column. Please try again.");
      },
    });

  const [updatePhaseMutation] = useUpdateKanbanPhaseMutation({
    optimisticResponse: variables => {
      const phase = phases.find(p => p.id === variables.id);
      return {
        __typename: "Mutation",
        updateKanbanPhase: {
          __typename: "KanbanPhase",
          ...phase!,
          name: variables.input.name!,
          updatedAt: new Date().toISOString(),
        },
      };
    },
    onError: () => {
      onOperationError("Failed to update column name. Please try again.");
    },
  });

  const [deletePhaseMutation] = useDeleteKanbanPhaseMutation({
    refetchQueries: ["ListKanbanPhases"],
    awaitRefetchQueries: true,
    onError: () => {
      onOperationError("Failed to delete column. Please try again.");
    },
  });

  const [updatePhasePositionsMutation] = useUpdateKanbanPhasePositionsMutation({
    optimisticResponse: variables => ({
      __typename: "Mutation",
      updateKanbanPhasePositions: variables.input.phasePositions.map(pp => {
        const phase = phases.find(p => p.id === pp.phaseId);
        return {
          __typename: "KanbanPhase",
          ...phase!,
          position: pp.position,
          updatedAt: new Date().toISOString(),
        };
      }),
    }),
    refetchQueries: ["ListKanbanPhases"],
    awaitRefetchQueries: false,
    onError: () => {
      onOperationError("Failed to reorder columns. Please try again.");
    },
  });

  // Card mutations
  const [addPlayerMutation] = useAddPlayerToKanbanMutation({
    onError: () => {
      onOperationError(MESSAGES.ERROR.ADD_PLAYER_FAILED);
    },
  });

  const [moveCardMutation] = useMoveKanbanCardMutation({
    onError: () => {
      onOperationError("Failed to move player. Please try again.");
    },
  });

  // SE-4685: within-column rank reorder. Positions reconcile by entity id via
  // InMemoryCache normalization; the optimisticResponse is built at the call
  // site (useKanbanCardOperations) where the full phase card set is known, so
  // it is complete against the UpdateKanbanCardPositions selection set. A
  // non-blocking ListKanbanCards refetch reconciles with the server after the
  // optimistic write, without gating the instant UI reflection.
  const [updateCardPositionsMutation] = useUpdateKanbanCardPositionsMutation({
    refetchQueries: ["ListKanbanCards"],
    awaitRefetchQueries: false,
    onError: () => {
      onOperationError("Failed to reorder players. Please try again.");
    },
  });

  const [removePlayerMutation] = useRemovePlayerFromKanbanMutation({
    refetchQueries: ["ListKanbanCards"],
    awaitRefetchQueries: true,
    onError: () => {
      onOperationError("Failed to remove player. Please try again.");
    },
  });

  // SE-5496: terminal close / reopen. Both reconcile the board list via a
  // ListKanbanCards refetch (the mutations return a bare KanbanCard while the
  // list node is PlayerWithKanban, so an optimisticResponse can't produce the
  // list shape — same reasoning as the add path, avoiding the SE-5058
  // error-code-13 flood). No `onError` toast here: the caller
  // (useKanbanCardOperations / the reopen dialog) interprets the thrown error
  // through `resolvePipelineError`, which distinguishes refresh/silent/toast
  // per `extensions.code` (e.g. CARD_ALREADY_CLOSED → refresh, not a failure).
  const [closeCardMutation] = useCloseKanbanCardToTerminalStageMutation({
    refetchQueries: ["ListKanbanCards"],
    awaitRefetchQueries: true,
  });

  const [reopenCardMutation] = useReopenKanbanCardMutation({
    refetchQueries: ["ListKanbanCards"],
    awaitRefetchQueries: true,
  });

  return useMemo(
    () => ({
      createBoardMutation,
      isCreatingBoard,
      updateBoardMutation,
      createPhaseMutation,
      isCreatingPhase,
      updatePhaseMutation,
      deletePhaseMutation,
      updatePhasePositionsMutation,
      addPlayerMutation,
      moveCardMutation,
      closeCardMutation,
      reopenCardMutation,
      updateCardPositionsMutation,
      removePlayerMutation,
    }),
    [
      createBoardMutation,
      isCreatingBoard,
      updateBoardMutation,
      createPhaseMutation,
      isCreatingPhase,
      updatePhaseMutation,
      deletePhaseMutation,
      updatePhasePositionsMutation,
      addPlayerMutation,
      moveCardMutation,
      closeCardMutation,
      reopenCardMutation,
      updateCardPositionsMutation,
      removePlayerMutation,
    ]
  );
};

export default useKanbanBoardMutations;
